import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useSolicitarRegistro2FA, useRegister } from '@/hooks/useAuth'
import { RegisterRequest } from '@/types'
import { Eye, EyeOff, Mail, Lock, User, UserPlus, Phone, CreditCard, Shield } from 'lucide-react'

type FormData = RegisterRequest & {
  code2FA?: string
}

const Register = () => {
  const [showPassword, setShowPassword] = useState(false)
  const [step, setStep] = useState<'dados' | 'codigo'>('dados')
  const [emailEnviado, setEmailEnviado] = useState('')
  const navigate = useNavigate()
  
  const solicitar2FAMutation = useSolicitarRegistro2FA()
  const registerMutation = useRegister()

  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm<FormData>()
  const nome = watch('nome')
  const email = watch('email')
  const senha = watch('senha')

  // Passo 1: Solicitar código 2FA
  const onSolicitarCodigo = async (data: FormData) => {
    try {
      await solicitar2FAMutation.mutateAsync({
        email: data.email,
        senha: data.senha,
        nome: data.nome || ''
      })
      setEmailEnviado(data.email)
      setStep('codigo')
      setValue('code2FA', '')
    } catch (error) {
      // Erro já tratado no hook
    }
  }

  // Passo 2: Registrar com código 2FA
  const onRegistrar = async (data: FormData) => {
    try {
      const response = await registerMutation.mutateAsync({
        ...data,
        code2FA: data.code2FA
      })
      
      // Após registro bem-sucedido, redirecionar para login com mensagem
      navigate('/login', { 
        state: { 
          message: response.message || 'Cadastro realizado! Aguardando aprovação de um administrador. Você receberá um e-mail quando sua conta for ativada.' 
        } 
      })
    } catch (error) {
      // Erro já tratado no hook
    }
  }

  const voltarParaDados = () => {
    setStep('dados')
    setEmailEnviado('')
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-primary-100">
            <UserPlus className="h-6 w-6 text-primary-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {step === 'dados' ? 'Crie sua conta' : 'Valide seu e-mail'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {step === 'dados' ? (
              <>
                Ou{' '}
                <Link
                  to="/login"
                  className="font-medium text-primary-600 hover:text-primary-500"
                >
                  faça login se já tem uma conta
                </Link>
              </>
            ) : (
              <>
                Código enviado para <span className="font-medium">{emailEnviado}</span>
              </>
            )}
          </p>
        </div>

        {step === 'dados' ? (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSolicitarCodigo)}>
            <div className="space-y-4">
              <div>
                <label htmlFor="nome" className="block text-sm font-medium text-gray-700">
                  Nome Completo *
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('nome', { required: 'Nome é obrigatório' })}
                    type="text"
                    className="input pl-10"
                    placeholder="Digite seu nome completo"
                  />
                </div>
                {errors.nome && (
                  <p className="mt-1 text-sm text-red-600">{errors.nome.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  E-mail *
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('email', {
                      required: 'E-mail é obrigatório',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'E-mail inválido'
                      }
                    })}
                    type="email"
                    className="input pl-10"
                    placeholder="Digite seu e-mail"
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="senha" className="block text-sm font-medium text-gray-700">
                  Senha *
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('senha', { 
                      required: 'Senha é obrigatória',
                      minLength: {
                        value: 6,
                        message: 'Senha deve ter pelo menos 6 caracteres'
                      }
                    })}
                    type={showPassword ? 'text' : 'password'}
                    className="input pl-10 pr-10"
                    placeholder="Digite sua senha"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {errors.senha && (
                  <p className="mt-1 text-sm text-red-600">{errors.senha.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="cpf" className="block text-sm font-medium text-gray-700">
                  CPF
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <CreditCard className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('cpf', {
                      pattern: {
                        value: /^\d{3}\.\d{3}\.\d{3}-\d{2}$/,
                        message: 'CPF deve estar no formato 000.000.000-00'
                      }
                    })}
                    type="text"
                    className="input pl-10"
                    placeholder="000.000.000-00"
                  />
                </div>
                {errors.cpf && (
                  <p className="mt-1 text-sm text-red-600">{errors.cpf.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="telefone" className="block text-sm font-medium text-gray-700">
                  Telefone
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    {...register('telefone')}
                    type="tel"
                    className="input pl-10"
                    placeholder="(00) 00000-0000"
                  />
                </div>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={solicitar2FAMutation.isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {solicitar2FAMutation.isLoading ? 'Enviando código...' : 'Continuar'}
              </button>
            </div>

            <div className="text-center">
              <p className="text-xs text-gray-500">
                Ao criar uma conta, você concorda com nossos{' '}
                <Link to="/terms" className="text-primary-600 hover:text-primary-500">
                  Termos de Uso
                </Link>{' '}
                e{' '}
                <Link to="/privacy" className="text-primary-600 hover:text-primary-500">
                  Política de Privacidade
                </Link>
              </p>
            </div>
          </form>
        ) : (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onRegistrar)}>
            <div className="rounded-md bg-blue-50 p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <Shield className="h-5 w-5 text-blue-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Código de verificação enviado
                  </h3>
                  <div className="mt-2 text-sm text-blue-700">
                    <p>
                      Enviamos um código de 6 dígitos para <strong>{emailEnviado}</strong>.
                      Por favor, verifique sua caixa de entrada e insira o código abaixo.
                    </p>
                    <p className="mt-2">
                      O código expira em <strong>5 minutos</strong>.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="code2FA" className="block text-sm font-medium text-gray-700">
                Código de Verificação (6 dígitos) *
              </label>
              <div className="mt-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Shield className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  {...register('code2FA', {
                    required: 'Código de verificação é obrigatório',
                    pattern: {
                      value: /^\d{6}$/,
                      message: 'Código deve ter exatamente 6 dígitos'
                    }
                  })}
                  type="text"
                  maxLength={6}
                  className="input pl-10 text-center text-2xl tracking-widest font-mono"
                  placeholder="000000"
                  autoComplete="off"
                />
              </div>
              {errors.code2FA && (
                <p className="mt-1 text-sm text-red-600">{errors.code2FA.message}</p>
              )}
            </div>

            <div className="space-y-3">
              <button
                type="submit"
                disabled={registerMutation.isLoading}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {registerMutation.isLoading ? 'Registrando...' : 'Finalizar Cadastro'}
              </button>
              
              <button
                type="button"
                onClick={voltarParaDados}
                disabled={registerMutation.isLoading}
                className="w-full flex justify-center py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
              >
                Voltar
              </button>
            </div>

            <div className="text-center">
              <p className="text-xs text-gray-500">
                Não recebeu o código?{' '}
                <button
                  type="button"
                  onClick={() => {
                    if (nome && email && senha) {
                      solicitar2FAMutation.mutate({ email, senha, nome })
                    }
                  }}
                  disabled={solicitar2FAMutation.isLoading}
                  className="text-primary-600 hover:text-primary-500 font-medium"
                >
                  Reenviar código
                </button>
              </p>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default Register
